import CheckIcon from '@material-ui/icons/Check';
import {
  DropdownMenuCheckboxItem as DropdownMenuCheckboxItemPrimitive,
  DropdownMenuContent as DropdownMenuContentPrimitive,
  DropdownMenuGroup as DropdownMenuGroupPrimitive,
  DropdownMenuItemIndicator,
  DropdownMenuItem as DropdownMenuItemPrimitive,
  DropdownMenuLabel as DropdownMenuLabelPrimitive,
  DropdownMenuPortal as DropdownMenuPortalPrimitive,
  DropdownMenu as DropdownMenuPrimitive,
  type DropdownMenuProps,
  DropdownMenuSeparator as DropdownMenuSeparatorPrimitive,
  DropdownMenuTrigger as DropdownMenuTriggerPrimitive,
  type DropdownMenuTriggerProps as DropdownMenuTriggerPropsPrimitive,
} from '@radix-ui/react-dropdown-menu';
import * as React from 'react';
import DropdownMenuIcon from './components/DropdownMenuIcon';

import cn from '../../utils/cn';
import Typography from '../Typography';

const DropdownMenu = (props: DropdownMenuProps) => {
  return <DropdownMenuPrimitive {...props} />;
};

const DropdownMenuTrigger = ({
  className,
  ...props
}: DropdownMenuTriggerPropsPrimitive) => {
  return (
    <DropdownMenuTriggerPrimitive
      className={cn('justify-content outline-0 outline-gray-black', className)}
      {...props}
    />
  );
};

const DropdownMenuGroup = DropdownMenuGroupPrimitive;

const DropdownMenuPortal = DropdownMenuPortalPrimitive;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuContentPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuContentPrimitive> & {
    portal?: boolean;
  }
>(({ className, sideOffset = 4, portal = true, ...props }, ref) => {
  const DropdownMenuContentWrapper = portal
    ? DropdownMenuPortalPrimitive
    : React.Fragment;

  return (
    <DropdownMenuContentWrapper>
      <DropdownMenuContentPrimitive
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={{ top: 40, right: 40, bottom: 40, left: 40 }}
        className={cn(
          `z-dropdown min-w-[8rem] gap-2.5 space-y-1 overflow-hidden rounded-md border border-gray-light bg-white px-1 py-1 shadow-lg
          data-[state=open]:animate-in
          data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0
          data-[state=open]:fade-in-0
          data-[state=closed]:zoom-out-95
          data-[state=open]:zoom-in-95
          data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2
          data-[side=right]:slide-in-from-left-2
          data-[side=top]:slide-in-from-bottom-2`,
          className
        )}
        {...props}
      />
    </DropdownMenuContentWrapper>
  );
});
DropdownMenuContent.displayName = DropdownMenuContentPrimitive.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItemPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuItemPrimitive> & {
    inset?: boolean;
    keepOpen?: boolean;
  }
>(({ className, inset, keepOpen = false, onSelect, ...props }, ref) => {
  return (
    <DropdownMenuItemPrimitive
      ref={ref}
      onSelect={(event) => (keepOpen ? event.preventDefault() : null)}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-1 rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus:bg-gray/10 focus:text-gray-dark data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  );
});
DropdownMenuItem.displayName = DropdownMenuItemPrimitive.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuCheckboxItemPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuCheckboxItemPrimitive>
>(({ className, children, checked, disabled, ...props }, ref) => (
  <DropdownMenuCheckboxItemPrimitive
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-md py-1.5 pl-8 pr-6 text-sm outline-none transition-colors focus:bg-gray/10 focus:text-gray-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
      className
    )}
    onSelect={(event) => event.preventDefault()}
    checked={checked}
    disabled={disabled}
    {...props}
  >
    <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
      <DropdownMenuItemIndicator>
        {!disabled ? (
          <CheckIcon style={{ width: '1rem', height: '1rem' }} />
        ) : null}
      </DropdownMenuItemIndicator>
    </span>
    {children}
  </DropdownMenuCheckboxItemPrimitive>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuCheckboxItemPrimitive.displayName;

interface DropdownMenuLabel
  extends React.ComponentPropsWithoutRef<typeof Typography> {
  inset?: boolean;
}

const DropdownMenuLabel = ({
  className,
  inset,
  children,
  ...props
}: DropdownMenuLabel) => (
  <Typography
    color='secondary'
    className={cn(inset && 'pl-8', 'flex items-center gap-1', className)}
    {...props}
  >
    {children}
  </Typography>
);

DropdownMenuLabel.displayName = DropdownMenuLabelPrimitive.displayName;

interface DropdownMenuHeader
  extends React.ComponentPropsWithoutRef<typeof Typography> {
  inset?: boolean;
}

const DropdownMenuHeader = ({
  className,
  inset,
  children,
  ...props
}: DropdownMenuHeader) => (
  <Typography
    variant='description-title'
    color='secondary'
    className={cn('px-2 py-1.5', inset && 'pl-8', className)}
    {...props}
  >
    {children}
  </Typography>
);

DropdownMenuHeader.displayName = 'DropdownMenuHeader';

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSeparatorPrimitive>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSeparatorPrimitive>
>(({ className, ...props }, ref) => (
  <DropdownMenuSeparatorPrimitive
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-gray-light', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuSeparatorPrimitive.displayName;

DropdownMenu.Icon = DropdownMenuIcon;
DropdownMenu.Trigger = DropdownMenuTrigger;
DropdownMenu.Content = DropdownMenuContent;
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.Header = DropdownMenuHeader;
DropdownMenu.CheckboxItem = DropdownMenuCheckboxItem;
DropdownMenu.Label = DropdownMenuLabel;
DropdownMenu.Separator = DropdownMenuSeparator;
DropdownMenu.Group = DropdownMenuGroup;
DropdownMenu.Portal = DropdownMenuPortal;

export default DropdownMenu;
